import { useCallback, useEffect, useRef, useState } from "react";
import { fetchGroupBasket } from "../api/groupBasketApi";
import { createUserSocket } from "../utils/userSocket";

/* While the socket is down the basket screen asks every 10 seconds (contract 7). */
export const GROUP_BASKET_POLL_MS = 10000;

/*
 * Should a `groupBasket:update` event make this screen fetch the basket?
 * Only for this basket, and only when the event's version is above the one
 * on screen (or the caller was removed).
 */
export const basketEventIsNewer = (event, basket, basketId) => {
  if (!event || String(event.id) !== String(basketId)) return false;
  if (event.removed === true) return true;
  const shown = Number(basket?.version);
  const incoming = Number(event.version);
  if (!Number.isFinite(incoming)) return true;
  return !Number.isFinite(shown) || incoming > shown;
};

/* A basket replaces the one on screen unless it is older. */
export const basketIsNewer = (incoming, shown) => {
  if (!incoming) return false;
  if (!shown || String(shown.id) !== String(incoming.id)) return true;
  return Number(incoming.version) >= Number(shown.version);
};

/**
 * One basket, live: fetched once, refetched whenever the server says it
 * changed (`groupBasket:update` on the customer's `user:<id>` room), and
 * every 10 seconds while that socket is down. Every answer passes the
 * version check, so a slow response never puts an older basket back.
 *
 * @param {object} args
 * @param {string} args.basketId
 * @param {string} args.userId     the signed-in customer (for the socket room)
 * @param {(previous, next) => void} [args.onChange] called on every newer basket
 * @returns {{ basket, status, error, receivedAt, refresh, apply, live }}
 *   status: "loading" | "ready" | "gone" | "failed"
 */
export default function useGroupBasket({ basketId, userId, onChange }) {
  const [state, setState] = useState({ basket: null, status: "loading", error: null, receivedAt: 0 });
  const [live, setLive] = useState(false);
  const basketRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const apply = useCallback((incoming) => {
    if (!basketIsNewer(incoming, basketRef.current)) return false;
    const previous = basketRef.current;
    basketRef.current = incoming;
    setState({ basket: incoming, status: "ready", error: null, receivedAt: Date.now() });
    if (onChangeRef.current) onChangeRef.current(previous, incoming);
    return true;
  }, []);

  const refresh = useCallback(async () => {
    if (!basketId) return null;
    try {
      const basket = await fetchGroupBasket(basketId);
      apply(basket);
      return basket;
    } catch (error) {
      if (error?.code === "BASKET_NOT_FOUND") {
        basketRef.current = null;
        setState({ basket: null, status: "gone", error, receivedAt: Date.now() });
      } else if (!basketRef.current) {
        setState({ basket: null, status: "failed", error, receivedAt: Date.now() });
      }
      return null;
    }
  }, [basketId, apply]);

  useEffect(() => {
    basketRef.current = null;
    setState({ basket: null, status: "loading", error: null, receivedAt: 0 });
    refresh();
  }, [refresh]);

  // The customer's own room carries every change to every basket they are in.
  useEffect(() => {
    if (!basketId || !userId) return undefined;
    const socket = createUserSocket();
    socket.on("connect", () => {
      socket.emit("join", `user:${userId}`);
      setLive(true);
      // Anything missed while the socket was down.
      refresh();
    });
    socket.on("disconnect", () => setLive(false));
    socket.on("connect_error", () => setLive(false));
    socket.on("groupBasket:update", (event) => {
      if (basketEventIsNewer(event, basketRef.current, basketId)) refresh();
    });
    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [basketId, userId, refresh]);

  useEffect(() => {
    if (live || !basketId) return undefined;
    const timer = setInterval(refresh, GROUP_BASKET_POLL_MS);
    return () => clearInterval(timer);
  }, [live, basketId, refresh]);

  // Back on the tab (after approving a payment prompt): ask at once.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  return { ...state, refresh, apply, live };
}
